import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, ExternalLink, Mail, MapPin, Phone, Plus, Star } from "lucide-react";
import Navbar, { MobileActionBar } from "../ui/Navbar";
import Footer from "../ui/Footer";
import ProjectCard, { availability } from "../ui/ProjectCard";
import UnitCard from "../ui/UnitCard";
import InquiryForm from "../ui/InquiryForm";
import Modal from "../ui/Modal";
import Lightbox from "../ui/Lightbox";
import { CallButton, EmailLink, MessengerButtons } from "../ui/ContactLinks";
import { getAllUnits, getProjects, getReviews } from "../firebase/api";
import { useAsync } from "../lib/hooks";
import { compareUnits, unitStatus, unitSummary } from "../lib/format";
import { trackContact } from "../lib/pixel";
import { track } from "../metrics/track";
import { SITE, mapEmbedSrc, mapLink } from "../config/site";
import { useI18n } from "../i18n";
import { useFormat } from "../i18n/format";
import Seo from "../seo/Seo";
import { apartmentComplex, faqPage } from "../seo/schema";
import heroBuilding from "../assets/pixel/hero-building.gif";
import blueprintGif from "../assets/pixel/blueprint.gif";
import bricksGif from "../assets/pixel/bricks.gif";
import craneGif from "../assets/pixel/crane.gif";
import keysGif from "../assets/pixel/keys.gif";
import workerGif from "../assets/pixel/worker.gif";
import pinGif from "../assets/pixel/location-pin.gif";
import teamLg from "../assets/img/tim-ledja-1920.jpg";
import teamSm from "../assets/img/tim-ledja-960.jpg";
import "../scss/home.scss";

const STEP_ART = [blueprintGif, bricksGif, workerGif, keysGif];

function Reveal({ as: Tag = "div", className = "", children, ...rest }) {
  return (
    <Tag className={`reveal ${className}`} {...rest}>
      {children}
    </Tag>
  );
}

function Stars({ value, label }) {
  return (
    <span className="stars" aria-label={label}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={16} fill={i <= value ? "currentColor" : "none"} className={i <= value ? "" : "is-empty"} />
      ))}
    </span>
  );
}

// Falls back to the author's initial when there is no photo or it fails to load.
function ReviewAvatar({ cover, author }) {
  const [failed, setFailed] = useState(false);
  return (
    <span className="review-avatar">
      {cover && !failed ? <img src={cover} alt="" loading="lazy" onError={() => setFailed(true)} /> : author?.[0]}
    </span>
  );
}

export default function Home() {
  const { t, lp, lang, field } = useI18n();
  const f = useFormat();
  const projects = useAsync(getProjects, []);
  const units = useAsync(
    () => (projects.data ? getAllUnits(projects.data) : Promise.resolve(null)),
    [projects.data]
  );
  const reviews = useAsync(getReviews, []);
  const [inquiryUnit, setInquiryUnit] = useState(null);
  const [gallery, setGallery] = useState(null);

  const totals = useMemo(
    () =>
      (projects.data || []).reduce(
        (acc, p) => {
          acc.flats += p.kapacitet_stanovi;
          acc.free += availability(p).freeFlats;
          return acc;
        },
        { flats: 0, free: 0 }
      ),
    [projects.data]
  );

  const projectById = useMemo(
    () => Object.fromEntries((projects.data || []).map((p) => [p.id, p])),
    [projects.data]
  );

  const featured = useMemo(
    () =>
      (units.data || [])
        .filter((u) => unitStatus(u) === "slobodan")
        .sort(compareUnits)
        .slice(0, 6),
    [units.data]
  );

  const minPrice = projects.data?.[0]?.najniza_cena;
  const faqVars = { price: minPrice || "1.200 €/m²", phone: SITE.phoneDisplay };
  const faqItems = t("home.faq.items").map((item) => ({ q: item.q, a: item.a.replace(/\{(\w+)\}/g, (m, k) => faqVars[k] ?? m) }));
  const stats = t("home.stats.items");
  const steps = t("home.process.steps");
  const values = t("home.about.values");

  const jsonLd = [
    faqPage(faqItems),
    ...(projects.data || []).map((p) =>
      apartmentComplex(p, { lang, description: field(p, "informacije_o_zgradi"), free: availability(p).freeFlats })
    ),
  ];

  return (
    <>
      <Seo
        title={t("seo.home.title")}
        description={t("seo.home.description")}
        path="/"
        jsonLd={jsonLd}
        ready={Boolean(units.data) && !reviews.loading}
      />
      <a href="#sadrzaj" className="skip-link">
        {t("common.skip")}
      </a>
      <Navbar />

      <main id="sadrzaj">
        {/* ---------- hero ---------- */}
        <section className="hero" id="pocetna">
          <div className="container hero-inner">
            <div className="hero-copy">
              <p className="eyebrow">{t("home.hero.eyebrow")}</p>
              <h1>{t("home.hero.title")}</h1>
              <p className="hero-lead">{t("home.hero.lead")}</p>
              <div className="hero-actions">
                <Link to={lp("/katalog")} className="btn btn--accent btn--lg">
                  {t("common.seeFreeUnits")} <ArrowRight size={18} />
                </Link>
                <CallButton className="btn btn--ghost btn--lg" source="hero">
                  {SITE.phoneDisplay}
                </CallButton>
              </div>
            </div>
            <div className="hero-art pixel-stage">
              <img
                src={heroBuilding}
                alt={t("home.hero.art")}
                className="pixel"
                width="96"
                height="72"
                fetchpriority="high"
              />
            </div>
          </div>
          <div className="container">
            <dl className="hero-facts" aria-label={t("home.facts.label")}>
              <div>
                <dt>{t("home.facts.offer")}</dt>
                <dd>{totals.flats ? t("home.facts.offerValue", { count: totals.flats }) : t("home.facts.offerFallback")}</dd>
              </div>
              <div>
                <dt>{t("home.facts.free")}</dt>
                <dd>{projects.loading ? "…" : t("project.freeFlats", { count: totals.free })}</dd>
              </div>
              <div>
                <dt>{t("home.facts.price")}</dt>
                <dd>{minPrice ? t("home.facts.priceFrom", { price: minPrice }) : t("common.priceOnRequest")}</dd>
              </div>
              <div>
                <dt>{t("home.facts.address")}</dt>
                <dd>{t("home.facts.addressValue")}</dd>
              </div>
            </dl>
          </div>
        </section>

        {/* ---------- buildings ---------- */}
        <section className="section" id="ponuda">
          <div className="container">
            <Reveal className="section-head">
              <p className="eyebrow">{t("home.projects.eyebrow")}</p>
              <h2>{t("home.projects.title")}</h2>
              <p>{t("home.projects.lead")}</p>
            </Reveal>
            <div className="project-grid">
              {projects.loading && [0, 1].map((i) => <div key={i} className="skeleton project-skeleton" />)}
              {projects.data?.map((project) => (
                <Reveal key={project.id}>
                  <ProjectCard project={project} />
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* ---------- featured units ---------- */}
        {featured.length > 0 && (
          <section className="section section--surface" id="stanovi">
            <div className="container">
              <Reveal className="section-head section-head--split">
                <div>
                  <p className="eyebrow">{t("home.featured.eyebrow")}</p>
                  <h2>{t("home.featured.title")}</h2>
                </div>
                <Link to={lp("/katalog")} className="btn btn--ghost">
                  {t("common.allUnits")} <ArrowRight size={18} />
                </Link>
              </Reveal>
              <div className="unit-grid">
                {featured.map((unit) => (
                  <UnitCard
                    key={`${unit.projectId}-${unit.id}`}
                    unit={unit}
                    project={projectById[unit.projectId]}
                    onInquire={setInquiryUnit}
                    onOpenGallery={(u) => setGallery({ images: u.imageUrls, title: f.unitSummary(u, projectById[u.projectId]) })}
                  />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ---------- how we build ---------- */}
        <section className="section" id="kako-gradimo">
          <div className="container">
            <Reveal className="section-head">
              <p className="eyebrow">{t("home.process.eyebrow")}</p>
              <h2>{t("home.process.title")}</h2>
              <p>{t("home.process.lead")}</p>
            </Reveal>
            <ol className="steps">
              {steps.map((step, i) => (
                <Reveal as="li" key={step.title} className="step">
                  <div className="step-art pixel-stage">
                    <img src={STEP_ART[i]} alt="" className="pixel" width="48" height="48" loading="lazy" />
                  </div>
                  <span className="step-num">{String(i + 1).padStart(2, "0")}</span>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </Reveal>
              ))}
            </ol>
          </div>
        </section>

        {/* ---------- track record ---------- */}
        <section className="section section--navy" id="realizovano">
          <div className="container record">
            <div>
              <Reveal className="section-head">
                <p className="eyebrow">{t("home.stats.eyebrow")}</p>
                <h2>{t("home.stats.title")}</h2>
              </Reveal>
              <dl className="stats">
                {stats.map((s) => (
                  <Reveal key={s.label}>
                    <dt>{s.value}</dt>
                    <dd>{s.label}</dd>
                  </Reveal>
                ))}
              </dl>
            </div>
            <div className="record-art" aria-hidden="true">
              <img src={craneGif} alt="" className="pixel" width="48" height="48" loading="lazy" />
            </div>
          </div>
        </section>

        {/* ---------- about ---------- */}
        <section className="section" id="o-nama">
          <div className="container about">
            <Reveal className="about-media">
              <img
                src={teamSm}
                srcSet={`${teamSm} 960w, ${teamLg} 1920w`}
                sizes="(min-width: 1024px) 50vw, 100vw"
                alt={t("home.about.photoAlt")}
                width="960"
                height="640"
                loading="lazy"
                decoding="async"
              />
              <div className="about-badge">
                <Building2 size={20} aria-hidden="true" />
                <span>
                  <strong>{t("common.company")}</strong>
                  {t("common.city")}
                </span>
              </div>
            </Reveal>
            <div className="about-content">
              <Reveal className="section-head">
                <p className="eyebrow">{t("home.about.eyebrow")}</p>
                <h2>{t("home.about.title")}</h2>
                <p>{t("home.about.lead")}</p>
              </Reveal>
              <ul className="values">
                {values.map((v, i) => (
                  <Reveal as="li" key={v.title}>
                    <span className="values-num">{String(i + 1).padStart(2, "0")}</span>
                    <div>
                      <h3>{v.title}</h3>
                      <p>{v.text}</p>
                    </div>
                  </Reveal>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* ---------- reviews ---------- */}
        {reviews.data?.length > 0 && (
          <section className="section section--surface" id="recenzije">
            <div className="container">
              <Reveal className="section-head">
                <p className="eyebrow">{t("home.reviews.eyebrow")}</p>
                <h2>{t("home.reviews.title")}</h2>
              </Reveal>
              <div className="review-grid">
                {reviews.data.map((review) => (
                  <Reveal key={review.id}>
                    <figure className="review-card">
                      <Stars value={Number(review.rating) || 5} label={t("home.reviews.rating", { value: Number(review.rating) || 5 })} />
                      <blockquote>„{field(review, "text")}“</blockquote>
                      <footer>
                        <ReviewAvatar cover={review.cover} author={review.author} />
                        <figcaption>{review.author}</figcaption>
                      </footer>
                    </figure>
                  </Reveal>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ---------- location ---------- */}
        <section className="section" id="lokacija">
          <div className="container location">
            <Reveal className="location-text">
              <div className="section-head">
                <p className="eyebrow">{t("home.location.eyebrow")}</p>
                <h2>{t("home.location.title")}</h2>
                <p>{t("home.location.lead")}</p>
              </div>
              <ul className="location-list">
                <li>
                  <MapPin size={20} aria-hidden="true" />
                  <div>
                    <strong>Lamela A</strong>
                    <span>{t("home.location.lamelaA")}</span>
                  </div>
                </li>
                <li>
                  <MapPin size={20} aria-hidden="true" />
                  <div>
                    <strong>Lamela B</strong>
                    <span>{t("home.location.lamelaB")}</span>
                  </div>
                </li>
                <li>
                  <Building2 size={20} aria-hidden="true" />
                  <div>
                    <strong>{t("home.location.office")}</strong>
                    <span>{SITE.office}</span>
                  </div>
                </li>
              </ul>
              <a
                href={mapLink(SITE.projectMapQuery)}
                className="btn btn--ghost"
                target="_blank"
                rel="noreferrer"
                onClick={() => track("map_open", { source: "lokacija" })}
              >
                {t("common.openMaps")} <ExternalLink size={16} />
              </a>
            </Reveal>
            <Reveal className="location-map">
              <div className="map-frame">
                <iframe
                  title={t("home.location.mapTitle")}
                  src={mapEmbedSrc(SITE.projectMapQuery)}
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
              <img src={pinGif} alt="" className="pixel location-pin" width="48" height="48" loading="lazy" />
            </Reveal>
          </div>
        </section>

        {/* ---------- FAQ ---------- */}
        <section className="section section--surface" id="pitanja">
          <div className="container faq">
            <Reveal className="section-head">
              <p className="eyebrow">{t("home.faq.eyebrow")}</p>
              <h2>{t("home.faq.title")}</h2>
            </Reveal>
            <div className="faq-list">
              {faqItems.map((item) => (
                <details key={item.q} className="faq-item">
                  <summary>
                    <h3>{item.q}</h3>
                    <Plus size={20} aria-hidden="true" />
                  </summary>
                  <p>{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ---------- contact ---------- */}
        <section className="section contact" id="kontakt">
          <div className="container contact-inner">
            <div className="contact-text">
              <p className="eyebrow">{t("home.contact.eyebrow")}</p>
              <h2>{t("home.contact.title")}</h2>
              <p>{t("home.contact.lead")}</p>
              <ul className="contact-list">
                <li>
                  <Phone size={20} aria-hidden="true" />
                  <a href={SITE.phoneHref} onClick={() => trackContact("Poziv", { source: "kontakt-lista" })}>
                    {SITE.phoneDisplay}
                  </a>
                </li>
                <li>
                  <Mail size={20} aria-hidden="true" />
                  <EmailLink source="kontakt" />
                </li>
                <li>
                  <MapPin size={20} aria-hidden="true" />
                  <span>{SITE.office}</span>
                </li>
              </ul>
              <div className="contact-actions">
                <CallButton className="btn btn--accent btn--lg" source="kontakt">
                  {t("common.callNow")}
                </CallButton>
                <MessengerButtons source="kontakt" className="btn btn--ghost btn--lg" />
              </div>
            </div>
            <div className="contact-card">
              <h3>{t("home.contact.formTitle")}</h3>
              <InquiryForm source="pocetna" idPrefix="kontakt" />
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <MobileActionBar />

      {inquiryUnit && (
        <Modal title={t("home.modalTitle")} onClose={() => setInquiryUnit(null)}>
          <InquiryForm
            source="pocetna"
            idPrefix="modal"
            unit={unitSummary(inquiryUnit, projectById[inquiryUnit.projectId])}
            unitLabel={f.unitSummary(inquiryUnit, projectById[inquiryUnit.projectId])}
          />
        </Modal>
      )}
      {gallery && (
        <Lightbox
          images={gallery.images}
          startIndex={gallery.start || 0}
          title={gallery.title}
          onClose={() => setGallery(null)}
        />
      )}
    </>
  );
}
